import React from 'react'
import { useBenefits } from '../BenefitsProvider'
import { useTranslation } from '~/hooks/useTranslation'
import YnsIcon from '~/components/icons/YnsIcon'
import { cn } from '~/lib/utils'
import { Modal, Stack } from '~/components/_layouts/index'
import { useIsTablet } from '~/hooks/useMediaQuery'

export const RightRailModal = ({
  resumeDocsModalOpen,
  setResumeDocsModalOpen,
}) => {
  return (
    <Modal
      title={
        <Stack direction="row" spacing={2}>
          <YnsIcon name="document" className="mr-2 h-6 w-6" />
          <ResumeDocumentsTitle />
        </Stack>
      }
      variant="secondary"
      open={resumeDocsModalOpen}
      onClose={() => setResumeDocsModalOpen(false)}
      size="md"
    >
      <ResumeDocuments />
    </Modal>
  )
}

const RightRail = () => {
  return (
    <div className="mb-4 mr-8 w-96 rounded-[20px] bg-intoo-green-coachcta p-8">
      <div className="subtitle mb-8 flex items-center text-white">
        <YnsIcon name="document" className="mr-2 h-6 w-6" />
        <ResumeDocumentsTitle />
      </div>

      <ResumeDocuments useInvertedColors />
    </div>
  )
}

export default RightRail

const ResumeDocumentsTitle = () => {
  const { t } = useTranslation('RightRail')
  return <>{t('title')}</>
}

const ResumeDocuments = ({ useInvertedColors = false }) => {
  const { t } = useTranslation('RightRail')
  const documentSteps = t('documents', { returnObjects: true })
  return (
    <>
      {documentSteps.map((step, idx) => (
        <DocumentStep
          key={step.key}
          documentStep={step}
          idx={idx}
          documentsLength={documentSteps.length}
          useInvertedColors={useInvertedColors}
        />
      ))}
    </>
  )
}

const Resume = ({ idx, useInvertedColors }) => {
  const { resumeReview, coachRevisions } = useBenefits()
  const isOriginalResume = idx === 0
  const anyDeclined = resumeReview?.revision_declined

  const getEffectiveIdx = () => {
    if (anyDeclined && idx > 2) return idx - 1
    if (resumeReview?.closed && idx >= coachRevisions?.length)
      return coachRevisions.length
    return idx
  }

  const effectiveIdx = getEffectiveIdx()
  const revision = coachRevisions[effectiveIdx - 1]

  const url = isOriginalResume
    ? resumeReview?.resume_url
    : revision?.remote_content_url
  const name = isOriginalResume
    ? resumeReview?.resume_name
    : revision?.remote_content

  const linkColorClass = useInvertedColors ? 'text-link-white' : 'text-link'

  return (
    <div className="h-20">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={name}
        className={cn('flex items-center', linkColorClass)}
      >
        <span
          className={cn('w-60 overflow-hidden text-ellipsis', linkColorClass)}
        >
          {name}
        </span>
        {name && (
          <YnsIcon
            name="download"
            className={cn('ml-2 h-6 w-6', linkColorClass)}
          />
        )}
      </a>
    </div>
  )
}

const DocumentStep = ({
  documentStep,
  documentsLength,
  idx,
  useInvertedColors = false,
  ...props
}) => {
  const { t } = useTranslation('RightRail')
  const { title } = documentStep
  const { resumeReview, coachRevisions } = useBenefits()

  const isOriginalResume = idx === 0
  const showBorder = idx < documentsLength - 1

  const anyDeclined = resumeReview?.revision_declined
  const comparisonIdx = idx > 2 && anyDeclined ? idx - 1 : idx
  const closed = resumeReview?.closed

  const revisionsCount = coachRevisions.length

  const closedBeforeThisRevision =
    closed && revisionsCount <= idx && idx !== documentsLength - 1

  const completed =
    closed ||
    (isOriginalResume
      ? !!resumeReview?.resume_name
      : revisionsCount >= comparisonIdx)

  const declined = resumeReview?.revision_declined && idx === 2

  const isTablet = useIsTablet()
  const textColorClass = useInvertedColors ? 'text-white' : null
  const linkColorClass = useInvertedColors ? 'text-link-white' : 'text-link'

  const noCoachDocument =
    resumeReview?.coach_started && !resumeReview?.resume_url && idx === 0

  let content = <Resume idx={idx} useInvertedColors={useInvertedColors} />
  if (noCoachDocument) {
    content = (
      <div className={cn('h-20 italic', textColorClass)}>
        {t('no_document')}
      </div>
    )
  } else if (closedBeforeThisRevision) {
    content = <div className="h-20"></div>
  } else if (declined) {
    content = (
      <div className={cn('h-20 italic', textColorClass)}>{t('declined')}</div>
    )
  }

  const borderBg = isTablet ? 'bg-intoo-green-dark top-4' : 'bg-white'

  return (
    <div {...props} className="mb-1 flex flex-col gap-1">
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex flex-shrink-0 items-center gap-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-willow-brook">
            {completed && (
              <div className="h-3 w-3 rounded-full bg-intoo-green-dark"></div>
            )}
          </div>
          <div className={textColorClass}>{title}:</div>
        </div>
      </div>
      <div className="relative">
        <div
          className={cn(
            showBorder && 'absolute left-3 h-full w-[.15rem] pb-1',
            borderBg,
          )}
        ></div>
        <div className={cn('ml-12 pb-4', linkColorClass)}>{content}</div>
      </div>
    </div>
  )
}
